import React, { ReactNode, useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'

import lang from '@/lang'
import DocumentPreview from '@/modules/signature/components/document-preview/signature-document-preview'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import SignatureSigningInfo from '@/modules/signature/components/signing/signature-signing-info/signature-signing-info'
import ThemeInterface from '@/theme-interface'
import { Creator, Signature, SignatureItem, Signer, File } from '@/modules/signature/types'
import SignatureSigningState from '@/modules/signature/components/signing/signature-signing-state/signature-signing-state'
import SignatureSigningDocument from '@/modules/signature/components/signing/signature-signing-document/signature-signing-document'
import SignatureSignings from '@/modules/signature/components/signing/signature-signings/signature-signings'
import { useAppSelector } from '@/redux/store'
import { ROLES, SIGNATURE_FIELD_TYPES, signatureTypes } from '@/modules/base/util/constants'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    overflow: 'auto',
    height: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.borderColors.default}`,
    minWidth: 497,
    maxWidth: 497,
  },
  seperator: {
    borderBottom: `1px solid ${theme.borderColors.default}`,
    margin: '0px 30px',
  },
  messageWrapper: {
    position: 'relative',
    borderBottom: `1px solid ${theme.borderColors.default}`,
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.backgroundColor,
    padding: 30,
  },
  messageLabel: {
    color: theme.labelLight,
    fontSize: theme.fontSizes.smaller,
    marginBottom: 12,
  },
  messageContent: {
    fontSize: theme.fontSizes.small,
    maxHeight: 80,
    paddingRight: 20,
  },
  state: {
    position: 'absolute',
    bottom: -15,
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
  },
  info: {
    padding: 30,
  },
  signaturesScroll: {
    height: 'auto',
  },
  signatures: {
    padding: 30,
  },
  documents: {
    padding: 30,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: theme.backgroundColor,
    borderTop: `1px solid ${theme.borderColors.default}`,
    height: 85,
    padding: '0px 30px',
  },
  rejectButton: {
    background: theme.white,
    marginRight: 20,
  },
}), { name: 'SignatureSigning' })

type SignatureSigningProps = {
  clerks: string[],
  creator: Creator,
  signers: Signer[],
  signatureItems: SignatureItem[],
  message: string,
  dueDate: string,
  file: File,
  readOnly: boolean,
  status: string,
  customActions: ReactNode,
  onReject: () => void,
  onSubmit: (signatures: Signature[]) => void,
  onAbort: () => void,
  onSend: (email: string) => void,
}

const SignatureSigning = (props: SignatureSigningProps) => {
  const classes = useStyles()
  const {
    creator,
    signers,
    signatureItems: propSignatureItems,
    message,
    dueDate,
    file,
    readOnly,
    customActions,
    status,
    clerks,
    onReject,
    onSubmit,
    onAbort,
    onSend,
  } = props
  const activeUser = useAppSelector(state => state.user.info)
  const subscriptions = useAppSelector((state) => state.tenant.info.subscriptions)
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant)
  const isOwnerTaxConsultant = useAppSelector(state => state.user?.info?.isOwnerTaxConsultant)
  const {
    setCurrentItemIndex,
    loadItems,
    items: contextSignatureItems,
    setSigningMode,
    resetContext,
  } = useSignatureContext()

  const isUserSigner = useMemo(() => {
    return signers.find((signer) => signer.email === activeUser.email)
  }, [activeUser, signers])

  const isUserCreator = activeUser.id === creator.id || isOwnerTaxConsultant
  const isTaxAdmin = !!activeUser?.roles?.includes(ROLES.TAX_CONSULTANT_ROLE_ADMIN)
  const canSignForClient = isTaxConsultant && (isOwnerTaxConsultant || isTaxAdmin || clerks?.includes(activeUser.id))

  const initialUserSignatureItems = useMemo(() => {
    if (isUserCreator || canSignForClient) { return propSignatureItems }
    return propSignatureItems.filter((item) => item.signerEmail === activeUser.email)
  }, [activeUser, isUserCreator, canSignForClient, propSignatureItems])

  const signatureItems = useMemo(() => {
    return initialUserSignatureItems.map((item, index) => {
      return { ...item, ...contextSignatureItems[index] }
    })
  }, [initialUserSignatureItems, contextSignatureItems])

  const doneSignatureItems = useMemo(() => {
    return signatureItems.filter((item) => item.signed || item.signature)
  }, [signatureItems])

  const allDone = useMemo(() => {
    // necessary because the creator could also be a signer and therefor sees all signatures
    const userSignatures = initialUserSignatureItems.filter((item) => item.signerEmail === activeUser.email)
    const doneSignatureItems = contextSignatureItems
      .filter((item) => item.signerEmail === activeUser.email)
      .filter((item) => item.signed || item.signature || item.date || item.text)
    return userSignatures.length === doneSignatureItems.length
  }, [contextSignatureItems, initialUserSignatureItems])
  const sortedSigners = useMemo(() => {
    return signers.reduce((result, signer) => {
      if (signer.id === activeUser.id) {
        result.unshift(signer)
        return result
      }

      result.push(signer)
      return result
    }, [])
  }, [activeUser, signers])

  useEffect(() => {
    return () => {
      resetContext()
    }
  }, [])

  useEffect(() => {
    if (!readOnly) {
      setSigningMode(true)
      setCurrentItemIndex(-1)
    }
  }, [readOnly])

  useEffect(() => {
    loadItems({ signatures: initialUserSignatureItems })
  }, [initialUserSignatureItems])

  const handleSubmitClick = (): void => {
    if (!allDone) { return }

    let finalSignatures = signatureItems

    if (isUserCreator || canSignForClient) {
      finalSignatures = signatureItems.filter((signature) => signature.signerEmail === activeUser.email || signature.signerType === signatureTypes.client)
    }

    const itemsToSign = finalSignatures.reduce((signatures, signature) => {
      if (signature.type !== SIGNATURE_FIELD_TYPES.SIGNATURE) {
        signatures.push(signature)
        return signatures
      }

      if (!signature.signature) {
        return signatures
      }

      signatures.push(signature)
      return signatures
    }, [])

    onSubmit?.(itemsToSign)
  }

  return (
    <div className={classes.root}>
      {file && (
        <DocumentPreview
          auth
          hideDelete
          file={file} />
      )}
      <div className={classes.wrapper}>
        <div className={classes.messageWrapper}>
          <div className={classes.message}>
            <span className={classes.messageLabel}>{lang.signature.signing.message.label}</span>
            <PerfectScrollbar>
              <div className={classes.messageContent}>
                {message || lang.signature.signing.message.defaultMessage}
              </div>
            </PerfectScrollbar>
          </div>
          {(isUserCreator || isUserSigner || canSignForClient) && (
            <div className={classes.state}>
              <div>
                <SignatureSigningState
                  status={status}
                  signatureItems={signatureItems}
                  isUserCreator={isUserCreator || canSignForClient} />
              </div>
            </div>
          )}
        </div>
        <div className={classes.content}>
          <div className={classes.info}>
            <SignatureSigningInfo
              signers={sortedSigners}
              dueDate={dueDate}
              onSend={isUserCreator && onSend} />
          </div>
          <div className={classes.seperator}></div>
          {(isUserSigner || isUserCreator || canSignForClient) && (
            <>
              <PerfectScrollbar options={{ scrollYMarginOffset: 10 }} className={classes.signaturesScroll}>
                <div className={classes.signatures}>
                  <SignatureSignings
                    isUserCreator={isUserCreator || canSignForClient}
                    readOnly={readOnly}
                    signatureItems={signatureItems}
                    doneSignatureItems={doneSignatureItems} />
                </div>
              </PerfectScrollbar>
              <div className={classes.seperator}></div>
            </>
          )}
          <div className={classes.documents}>
            <SignatureSigningDocument document={file} />
          </div>

        </div>
        <div className={classes.footer}>
          {customActions && customActions}
          {(readOnly || (!isUserSigner && !isUserCreator && !canSignForClient)) && (
            <ButtonIconSquare
              label={lang.closeButtonLabel}
              onClick={onAbort} />
          )}
          {(!readOnly && (isUserSigner || isUserCreator || canSignForClient)) && (
            <>
              <ButtonIconSquare
                customClass={classes.rejectButton}
                label={lang.signature.signing.buttons.reject}
                color="red"
                variant="outline"
                onClick={onReject} />

              <ButtonIconSquare
                disabled={!allDone}
                label={lang.signature.signing.buttons.submit}
                onClick={handleSubmitClick} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SignatureSigning
